/** Material commitments and native purchase handoff for a work-order requirement. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { useItem, useLocations } from "@/features/inventory/queries";
import {
  useChangePartQuantity,
  useRequestPurchase,
  useReservePart,
} from "@/features/inventory/procurementQueries";
import type { Part, WorkOrderDetail } from "@/features/workorders/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { formatQuantity } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Field, Input, Select, Textarea } from "@/shared/ui/form";

type Action = "reserve" | "release" | "quantity" | "purchase";

type AvailabilityStatus =
  | "location_required"
  | "out_of_stock"
  | "sufficient"
  | "stock_elsewhere"
  | "insufficient_local"
  | "not_visible";

interface AvailableLocation {
  location_id: string;
  location_code: string;
  location_path: string;
  available_quantity: string;
}

type PartAvailability = Omit<Part, "available_quantity" | "shortage_quantity"> & {
  availability_status?: AvailabilityStatus;
  available_locations?: AvailableLocation[];
  available_quantity: string | null;
  shortage_quantity: string | null;
};

/** Location choice limited to scoped locations that can supply this item. */
export function PartStockLocationSelect({
  id,
  itemId,
  siteId,
  value,
  onChange,
  required = false,
  disabled = false,
}: {
  id: string;
  itemId: string;
  siteId?: string | null;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const item = useItem(itemId, can("inventory.stock.view"));
  const locations = useLocations();
  const scopedLocations = new Map(
    (locations.data ?? [])
      .filter((location) => !siteId || location.site_id === siteId)
      .map((location) => [location.id, location]),
  );
  const options = (item.data?.stock ?? []).filter(
    (level) =>
      scopedLocations.has(level.location_id) &&
      (Number(level.available) > 0 || level.location_id === value),
  );
  const permissionMissing = !can("inventory.stock.view");
  return (
    <div className="space-y-2">
      <Select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        disabled={disabled || permissionMissing || item.isPending || locations.isPending}
      >
        <option value="">{t("feedbackInventory.chooseLocation")}</option>
        {options.map((level) => {
          const location = scopedLocations.get(level.location_id);
          if (!location) return null;
          return (
            <option
              key={level.location_id}
              value={level.location_id}
              disabled={Number(level.available) <= 0}
            >
              {location.code} · {location.path} ·{" "}
              {t("feedbackInventory.availableAt", {
                quantity: formatQuantity(level.available),
              })}
            </option>
          );
        })}
      </Select>
      {permissionMissing ? (
        <p className="text-sm text-muted-foreground">{t("feedbackInventory.notVisible")}</p>
      ) : null}
      {item.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(item.error)}
        </p>
      ) : null}
      {locations.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(locations.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Auditable reservation, release, quantity edit or purchase request. */
function CommitmentDialog({
  wo,
  part,
  action,
  onClose,
}: {
  wo: WorkOrderDetail;
  part: Part;
  action: Action;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const reserve = useReservePart(wo.id);
  const change = useChangePartQuantity(wo.id);
  const purchase = useRequestPurchase();
  const form = useForm({
    initial: {
      quantity:
        action === "release"
          ? "0"
          : action === "purchase"
            ? String(Math.max(0, Number(part.quantity) - Number(part.reserved_quantity)))
            : part.quantity,
      stock_location_id: part.stock_location_id ?? "",
      reason: "",
    },
    idPrefix: "commitment",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      if (action === "purchase") {
        await purchase.mutateAsync({
          part_id: part.id,
          quantity: Number(v.quantity),
          reason: v.reason,
        });
      } else if (action === "quantity") {
        await change.mutateAsync({
          partId: part.id,
          body: {
            item_id: part.item_id,
            quantity: Number(v.quantity),
            stock_location_id: part.stock_location_id,
          },
        });
      } else {
        await reserve.mutateAsync({
          partId: part.id,
          body: {
            quantity: action === "release" ? 0 : Number(v.quantity),
            stock_location_id: v.stock_location_id || null,
          },
        });
      }
    });
    if (ok) onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(`workOrders.partActions.${action}`)}
      description={`${part.item_sku} · ${part.item_name}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      {action === "release" ? (
        <p>{t("workOrders.partReleaseHint")}</p>
      ) : (
        <Field id="commitment-quantity" label={t("app.quantity")} error={form.errors.quantity}>
          <Input type="number" step="0.001" min="0.001" {...form.field("quantity")} required />
        </Field>
      )}
      {action === "reserve" ? (
        <Field
          id="commitment-stock_location_id"
          label={t("inventory.location")}
          error={form.errors.stock_location_id}
        >
          <PartStockLocationSelect
            id="commitment-stock_location_id"
            itemId={part.item_id}
            siteId={wo.site?.id ?? null}
            value={form.values.stock_location_id}
            onChange={(id) => form.set("stock_location_id", id)}
            required
          />
        </Field>
      ) : null}
      {action === "purchase" ? (
        <Field
          id="commitment-reason"
          label={t("inventory.procurement.reason")}
          error={form.errors.reason}
        >
          <Textarea {...form.field("reason")} required />
        </Field>
      ) : null}
    </FormDialog>
  );
}

/** Stock commitment quantities stay visible independently of cost/adjustment permissions. */
export function PartCommitmentSummary({ part }: { part: Part }): ReactElement | null {
  const { t } = useTranslation();
  if (!["planned", "reserved"].includes(part.status)) return null;
  const scopedPart = part as PartAvailability;
  const availabilityStatus = scopedPart.availability_status ?? "not_visible";
  const canShowAvailability = availabilityStatus !== "not_visible";
  return (
    <dl className="space-y-1 text-xs">
      <div>
        <dt className="inline">{t("workOrders.partReserved")}: </dt>
        <dd className="inline">{formatQuantity(part.reserved_quantity)}</dd>
      </div>
      <div className={availabilityStatus === "stock_elsewhere" ? "font-medium" : ""}>
        {t(`feedbackInventory.availability.${availabilityStatus}`)}
      </div>
      {canShowAvailability && scopedPart.available_quantity !== null ? (
        <div>
          <dt className="inline">{t("workOrders.partAvailable")}: </dt>
          <dd className="inline">{formatQuantity(scopedPart.available_quantity)}</dd>
        </div>
      ) : null}
      {canShowAvailability && scopedPart.shortage_quantity !== null ? (
        <div
          className={Number(scopedPart.shortage_quantity) > 0 ? "font-medium text-destructive" : ""}
        >
          <dt className="inline">{t("workOrders.partShortage")}: </dt>
          <dd className="inline">{formatQuantity(scopedPart.shortage_quantity)}</dd>
        </div>
      ) : null}
      {canShowAvailability && scopedPart.available_locations?.length ? (
        <ul className="ps-4">
          {scopedPart.available_locations.map((location) => (
            <li key={location.location_id}>
              {location.location_path} ·{" "}
              {t("feedbackInventory.availableAt", {
                quantity: formatQuantity(location.available_quantity),
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </dl>
  );
}

/** Planner/storekeeper controls without general inventory adjustment privileges. */
export function PartCommitmentActions({
  wo,
  part,
}: {
  wo: WorkOrderDetail;
  part: Part;
}): ReactElement | null {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [action, setAction] = useState<Action | null>(null);
  if (
    ["completed", "verified", "closed", "cancelled"].includes(wo.status) ||
    !["planned", "reserved"].includes(part.status)
  )
    return null;
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {action ? (
        <CommitmentDialog wo={wo} part={part} action={action} onClose={() => setAction(null)} />
      ) : null}
      {can("workorders.parts.request") ? (
        <>
          <Button size="sm" variant="ghost" onClick={() => setAction("reserve")}>
            {t("workOrders.partActions.reserve")}
          </Button>
          {Number(part.reserved_quantity) > 0 ? (
            <Button size="sm" variant="ghost" onClick={() => setAction("release")}>
              {t("workOrders.partActions.release")}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setAction("quantity")}>
            {t("workOrders.partActions.quantity")}
          </Button>
        </>
      ) : null}
      {can("procurement.request.create") ? (
        <Button size="sm" variant="ghost" onClick={() => setAction("purchase")}>
          {t("workOrders.partActions.purchase")}
        </Button>
      ) : null}
    </div>
  );
}
